import type {
  BriqpayUpdateSessionRequest,
  KlarnaOrder,
  KlarnaUpdateOrderRequest,
  PaymentProviderName,
  QliroUpdateOrderRequest,
  SveaUpdateOrderModel,
  UpdateOrderPayload
} from 'ecosystem'
import { briqpayApi, klarnaApiCall, qliroApi, sveaApi } from 'api'
import { nexiApi } from 'api/utils/nexi'
import { type UpdateOrderProviderAdapter } from './types'

type KlarnaPayload = NonNullable<UpdateOrderPayload['klarnaPayload']>
type NexiPayload = NonNullable<UpdateOrderPayload['nexiPayload']>
type SveaPayload = NonNullable<UpdateOrderPayload['sveaPayload']>
type QliroPayload = NonNullable<UpdateOrderPayload['qliroPayload']>
type BriqpayPayload = NonNullable<UpdateOrderPayload['briqpayPayload']>

/**
 * Klarna replaces the whole order on update, so the current one is read back
 * first and the caller's fields merged onto it — sending the delta alone would
 * blank every field it omits.
 */
const klarnaAdapter: UpdateOrderProviderAdapter<KlarnaPayload> = {
  payloadKey: 'klarnaPayload',
  update: async ({ orderId, ...updatedOrder }) => {
    const { error: getOrderError, data: currentKlarnaOrder } = await klarnaApiCall<
      KlarnaOrder,
      undefined
    >('get', { orderId })

    if (getOrderError || !currentKlarnaOrder) {
      return { error: getOrderError || 'Unknown error' }
    }

    const payload: KlarnaUpdateOrderRequest = { ...currentKlarnaOrder, ...updatedOrder }

    const { error, data } = await klarnaApiCall<KlarnaOrder, KlarnaUpdateOrderRequest>('update', {
      payload,
      orderId
    })

    if (error || !data) {
      return { error: error || 'Unknown error' }
    }

    return { response: { klarnaResponse: data } }
  }
}

const nexiAdapter: UpdateOrderProviderAdapter<NexiPayload> = {
  payloadKey: 'nexiPayload',
  update: async ({ paymentId, ...updatedOrder }) => {
    // Nexi's update endpoint answers with no body, so success is reported as a
    // bare `true` rather than an echoed order.
    // @see https://developer.nexigroup.com/nexi-checkout/en-EU/docs/add-shipping-cost/
    const { error } = await nexiApi.checkout.updateOrder(paymentId, {
      ...updatedOrder,
      shipping: { costSpecified: true }
    })

    return error ? { error } : { response: { nexiResponse: true } }
  }
}

const sveaAdapter: UpdateOrderProviderAdapter<SveaPayload> = {
  payloadKey: 'sveaPayload',
  update: async ({ orderId, ...updatedOrder }) => {
    const { error, data } = await sveaApi.checkout.updateOrder(
      orderId,
      updatedOrder as SveaUpdateOrderModel
    )

    return error ? { error } : { response: { sveaResponse: data ?? undefined } }
  }
}

const qliroAdapter: UpdateOrderProviderAdapter<QliroPayload> = {
  payloadKey: 'qliroPayload',
  update: async ({ orderId, ...updatedOrder }) => {
    const { error, data } = await qliroApi.checkout.updateOrder(
      orderId,
      updatedOrder as Omit<QliroUpdateOrderRequest, 'MerchantApiKey'>
    )

    return error ? { error } : { response: { qliroResponse: data ?? undefined } }
  }
}

const briqpayAdapter: UpdateOrderProviderAdapter<BriqpayPayload> = {
  payloadKey: 'briqpayPayload',
  update: async ({ sessionId, ...updatedSession }) => {
    // Party data (`billing`, `shipping`, `company`) is rejected while the matching
    // module is loaded in the client, and the rejection fails the whole payload — so
    // callers send cart updates and address updates as separate requests.
    const { error, data } = await briqpayApi.checkout.updateSession(
      sessionId,
      updatedSession as BriqpayUpdateSessionRequest
    )

    return error ? { error } : { response: { briqpayResponse: data ?? undefined } }
  }
}

/**
 * LEDYER is deliberately absent — it has no implementation yet, and the entry
 * point turns the missing key into the same "not implemented" error the pages
 * version threw.
 */
export const UPDATE_ORDER_ADAPTERS: Partial<
  Record<PaymentProviderName, UpdateOrderProviderAdapter<any>>
> = {
  KLARNA: klarnaAdapter,
  NEXI: nexiAdapter,
  SVEA: sveaAdapter,
  QLIRO: qliroAdapter,
  BRIQPAY: briqpayAdapter
}
